'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';

export type SortBy = 'alphabetical' | 'updated';
export type SortDirection = 'asc' | 'desc';

interface SearchSortBarProps {
    searchPlaceholder?: string;
    searchValue: string;
    onSearchChange: (value: string) => void;
    sortBy: SortBy;
    sortDirection: SortDirection;
    onSortChange: (by: SortBy, direction: SortDirection) => void;
    showUpdatedSort?: boolean;
}

export function SearchSortBar({
    searchPlaceholder,
    searchValue,
    onSearchChange,
    sortBy,
    sortDirection,
    onSortChange,
    showUpdatedSort = true,
}: SearchSortBarProps) {
    const t = useTranslations('common');
    const [localSearch, setLocalSearch] = useState(searchValue);
    const isClearing = useRef(false);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            // Skip debounce if we just cleared the search via the clear button
            if (isClearing.current && localSearch === '') {
                isClearing.current = false;
                return;
            }
            onSearchChange(localSearch);
        }, 300);
        return () => clearTimeout(timer);
    }, [localSearch, onSearchChange]);

    // Sync external changes
    useEffect(() => {
        setLocalSearch(searchValue);
    }, [searchValue]);

    const handleAlphabeticalClick = useCallback(() => {
        if (sortBy === 'alphabetical') {
            onSortChange('alphabetical', sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            onSortChange('alphabetical', 'asc');
        }
    }, [sortBy, sortDirection, onSortChange]);

    const handleUpdatedClick = useCallback(() => {
        if (sortBy === 'updated') {
            onSortChange('updated', sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            onSortChange('updated', 'desc'); // Default to newest first
        }
    }, [sortBy, sortDirection, onSortChange]);

    const handleClearSearch = useCallback(() => {
        isClearing.current = true;
        setLocalSearch('');
        onSearchChange('');
    }, [onSearchChange]);

    const getSortIcon = (isActive: boolean, direction: SortDirection, type: 'alpha' | 'date') => {
        if (!isActive) {
            return type === 'alpha' ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                </svg>
            ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        }

        return direction === 'asc' ? (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
        ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
        );
    };

    return (
        <div className="flex items-center gap-2">
            {/* Search Input */}
            <div className="relative">
                <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                </svg>
                <input
                    type="text"
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    placeholder={searchPlaceholder || t('searchPlaceholder')}
                    className="w-96 pl-9 pr-9 py-2 text-sm border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-slate-300 transition-colors"
                />
                {localSearch && (
                    <button
                        onClick={handleClearSearch}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                        title="Clear search"
                        type="button"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Alphabetical Sort Button */}
            <button
                onClick={handleAlphabeticalClick}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${sortBy === 'alphabetical'
                        ? 'bg-black text-white'
                        : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                title={t('sortAlphabetical')}
            >
                {getSortIcon(sortBy === 'alphabetical', sortDirection, 'alpha')}
            </button>

            {/* Last Updated Sort Button */}
            {showUpdatedSort && (
                <button
                    onClick={handleUpdatedClick}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${sortBy === 'updated'
                            ? 'bg-black text-white'
                            : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                    title={t('sortLastUpdated')}
                >
                    {getSortIcon(sortBy === 'updated', sortDirection, 'date')}
                </button>
            )}
        </div>
    );
}
